import React from 'react';

import { alpha, Box, styled, Typography, useThemeProps } from '@mui/material';

import { palette } from '@app/theme';

import { type NoteOwnerState, type NoteProperties, type NoteVariant } from './Note.types';

const NoteRoot = styled('div', {
  name: 'Note',
  slot: 'root',
})<{ ownerState: NoteOwnerState }>(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderLeft: '4px solid',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  variants: [
    {
      props: { variant: 'error' },
      style: {
        borderColor: theme.palette.error.main,
        backgroundColor: alpha(theme.palette.error.main, 0.06),
      },
    },
    {
      props: { variant: 'warning' },
      style: {
        borderColor: theme.palette.warning.main,
        backgroundColor: alpha(theme.palette.warning.main, 0.06),
      },
    },
    {
      props: { variant: 'info' },
      style: {
        borderColor: theme.palette.info.main,
        backgroundColor: alpha(theme.palette.info.main, 0.06),
      },
    },
    {
      props: { variant: 'success' },
      style: {
        borderColor: theme.palette.success.main,
        backgroundColor: alpha(theme.palette.success.main, 0.06),
      },
    },
  ],
}));

const iconColors: Record<NoteVariant, string> = {
  error: palette.error.main,
  warning: palette.warning.main,
  info: palette.primary.main,
  success: palette.success.main,
};

export const Note = React.forwardRef<HTMLDivElement, NoteProperties>(
  function Note(inProperties, reference) {
    const properties = useThemeProps({ props: inProperties, name: 'Note' });
    const {
      dataTest = 'note',
      variant = 'error',
      noteTitle,
      children,
      id,
      sx,
    } = properties;

    const ownerState: NoteOwnerState = { variant };

    return (
      <NoteRoot
        data-testid={dataTest}
        ownerState={ownerState}
        ref={reference}
        id={id ?? dataTest}
        sx={sx}
      >
        {/* Status icon */}
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: iconColors[variant],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'common.white',
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1 }}>
          {noteTitle && (
            <Typography
              data-testid={`${dataTest}_title`}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'text.primary',
                mb: 0.25,
              }}
            >
              {`${noteTitle}:`}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: 'text.secondary',
              lineHeight: 1.5,
            }}
          >
            {children}
          </Typography>
        </Box>
      </NoteRoot>
    );
  },
);
